import type { Meta, StoryObj } from '@storybook/angular';
import { expect } from 'storybook/test';
import { SuitcaseIconComponent } from '../../../app/core/svg-icons/suitcase-icon.component';

const meta: Meta<SuitcaseIconComponent> = {
  title: 'Atoms/Icons/SuitcaseIcon',
  component: SuitcaseIconComponent,
  parameters: {
    docs: {
      description: {
        component: 'A customizable suitcase icon component. Perfect for business, travel, work-related features, or professional services sections.',
      },
    },
  },
  argTypes: {
    width: {
      control: { type: 'text' },
      description: 'Width of the icon',
    },
    height: {
      control: { type: 'text' },
      description: 'Height of the icon',
    },
    color: {
      control: { type: 'color' },
      description: 'Color of the suitcase icon',
    },
  },
  args: {
    width: '25px',
    height: '27px',
    color: 'white',
  },
};

export default meta;
type Story = StoryObj<SuitcaseIconComponent>;

export const Default: Story = {
  args: {
    width: '25px',
    height: '27px',
    color: 'white',
  },
  render: (args) => ({
    props: args,
    template: `<app-suitcase-icon 
      [width]="width" 
      [height]="height" 
      [color]="color">
    </app-suitcase-icon>`,
  }),
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-suitcase-icon')).toBeTruthy();
  },
};

export const BusinessSection: Story = {
  args: {
    width: '32px',
    height: '32px',
    color: '#5C0A31',
  },
  render: (args) => ({
    props: args,
    template: `
      <div style="border: 1px solid #ddd; border-radius: 8px; padding: 2rem; text-align: center; max-width: 300px;">
        <app-suitcase-icon 
          [width]="width" 
          [height]="height" 
          [color]="color">
        </app-suitcase-icon>
        <h4 style="margin: 1rem 0 0.5rem 0; color: #5C0A31;">Erhvervskunder</h4>
        <p style="color: #666; margin: 0;">Særlige tilbud og personlig service til virksomheder</p>
      </div>
    `,
  }),
  parameters: {
    docs: {
      description: {
        story: 'Suitcase icon used in business customer section.',
      },
    },
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-suitcase-icon')).toBeTruthy();
  },
};

export const InNavigation: Story = {
  args: {
    width: '24px',
    height: '24px',
    color: 'white',
  },
  render: (args) => ({
    props: args,
    template: `
      <nav style="background-color: #5C0A31; padding: 1rem;">
        <div style="display: flex; align-items: center; gap: 1rem; color: white;">
          <app-suitcase-icon 
            [width]="width" 
            [height]="height" 
            [color]="color">
          </app-suitcase-icon>
          <span>Business Portal</span>
        </div>
      </nav>
    `,
  }),
  parameters: {
    docs: {
      description: {
        story: 'Suitcase icon in dark navigation for business portal access.',
      },
    },
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-suitcase-icon')).toBeTruthy();
  },
};

export const ColorVariants: Story = {
  render: () => ({
    template: `
      <div style="display: flex; gap: 2rem; align-items: center; flex-wrap: wrap;">
        <div style="text-align: center;">
          <div>Wine Red</div>
          <app-suitcase-icon width="32px" height="32px" color="#5C0A31"></app-suitcase-icon>
        </div>
        <div style="text-align: center;">
          <div>Dark Gray</div>
          <app-suitcase-icon width="32px" height="32px" color="#333"></app-suitcase-icon>
        </div>
        <div style="text-align: center;">
          <div>Orange</div>
          <app-suitcase-icon width="32px" height="32px" color="#F39C12"></app-suitcase-icon>
        </div>
        <div style="text-align: center; background-color: #5C0A31; padding: 1rem; border-radius: 8px;">
          <div style="color: white; margin-bottom: 0.5rem;">White</div>
          <app-suitcase-icon width="32px" height="32px" color="white"></app-suitcase-icon>
        </div>
      </div>
    `,
  }),
  parameters: {
    docs: {
      description: {
        story: 'Different color variants of the suitcase icon.',
      },
    },
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-suitcase-icon')).toBeTruthy();
  },
};

export const SizeVariants: Story = {
  render: () => ({
    template: `
      <div style="display: flex; gap: 2rem; align-items: center;">
        <div style="text-align: center;">
          <div>Small (16px)</div>
          <app-suitcase-icon width="16px" height="16px" color="#5C0A31"></app-suitcase-icon>
        </div>
        <div style="text-align: center;">
          <div>Medium (24px)</div>
          <app-suitcase-icon width="24px" height="24px" color="#5C0A31"></app-suitcase-icon>
        </div>
        <div style="text-align: center;">
          <div>Default (25px)</div>
          <app-suitcase-icon width="25px" height="27px" color="#5C0A31"></app-suitcase-icon>
        </div>
        <div style="text-align: center;">
          <div>Large (40px)</div>
          <app-suitcase-icon width="40px" height="40px" color="#5C0A31"></app-suitcase-icon>
        </div>
      </div>
    `,
  }),
  parameters: {
    docs: {
      description: {
        story: 'Different size variants of the suitcase icon.',
      },
    },
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-suitcase-icon')).toBeTruthy();
  },
};
